/** @format */

import React, { useEffect, useState } from 'react'
import localize from '../../../../../Global/Localize'
import BreadCrumb from '../../../../../Components/BreadCrumb/Index'
import GroupedTable from '../../../../../Components/Tables/GroupedTable'
import { getData } from '../../../../../Helpers/webApi'
import Pager from '../../../../../Components/Tables/Pager'
import EnumsServerCombo from '../../../../../Components/ComboBox/EnumsServerCombo'
import DatePicker from '../../../../../Components/Date/DatePicker'
import Loader from '../../../../../Components/Loaders/Loader'
import { formatCurrency, formatDate } from '../../../../../Helpers/formatters'
import ViewAuditLogs from '../../../../../Components/ReUsables/ViewAuditLogs/ViewAuditLogs'
import RecordsPerPage from '../../../../../Components/RecordsPerPage/RecordsPerPage'

const MobileBankingRequests = () => {
  const [isBusy, setIsBusy] = useState(false)
  const [requestLogs, setRequestLogs] = useState(null)
  const [error, setError] = useState(false)
  const [modalMode, setModalMode] = useState(null)
  const [auditLogsDetails, setAuditLogsDetails] = useState({})
  const [groupBy, setGroupBy] = useState({
    label: 'Status',
    value: 2,
    column: 'statusDescription',
  })
  const [reqParams, setReqParams] = useState({
    startDate: null,
    endDate: null,
    filterText: '',
    pageIndex: 0,
    pageSize: 10,
    dateRequestsFilter: 15,
    customDate: false,
  })

  const fetchRequestLog = async () => {
    setIsBusy(true)
    const url =
      '/BankToMobileRequest/find-bank-to-mobile-requests-by-date-range-and-filter-in-page'

    const { success, data } = await getData(url, reqParams, false)

    if (success) {
      setRequestLogs(data)
    } else {
      setError(true)
      setIsBusy(false)
    }
    setIsBusy(false)
  }

  useEffect(() => {
    fetchRequestLog()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqParams])

  const columns = [
    {
      label: localize('createdDate'),
      template: r => <>{formatDate(r?.createdDate)}</>,
    },
    {
      label: localize('status'),
      template: r => <>{r?.statusDescription}</>,
    },
    {
      label: localize('transactionType'),
      template: r => <>{r?.transactionTypeDescription}</>,
    },
    {
      label: localize('transactionAccountNumber'),
      template: r => <>{r?.accountNumber}</>,
    },
    {
      label: localize('transactionCode'),
      template: r => <>{r?.transactionCode}</>,
    },
    {
      label: localize('transactionAmount'),
      template: r => <>{formatCurrency(r?.transactionAmount)}</>,
    },
    {
      label: localize('customerName'),
      template: r => <>{r?.customerFullName}</>,
    },
    {
      label: localize('customerAccountNumber'),
      template: r => <>{r?.paddedCustomerAccountCustomerAccountNumber}</>,
    },
    {
      label: localize('customerMobileLine'),
      template: r => <>{r?.customerAccountCustomerAddressMobileLine}</>,
    },
    {
      label: localize('outgoingStatusCode'),
      template: r => <>{r?.outgoingStatusCode}</>,
    },
    {
      label: localize('outgoingStatusDescription'),
      template: r => <>{r?.outgoingStatusDescription}</>,
    },
    {
      label: localize('outgoingNarration'),
      template: r => <>{r?.outgoingNarration}</>,
    },
    {
      label: localize('outgoingAvailableBalance'),
      template: r => <>{r?.outgoingAvailableBalance}</>,
    },
    {
      label: localize('outgoingInterestBalance'),
      template: r => <>{r?.outgoingInterestBalance}</>,
    },
    {
      label: localize('outgoingLoanLimit'),
      template: r => <>{r?.outgoingLoanLimit}</>,
    },
    {
      label: localize('callbackPayload'),
      template: r => <>{r?.callbackPayload}</>,
    },
    {
      label: localize('ipnEnabled') + '?',
      template: r => (
        <span className="text-capitalize">{r?.ipnEnabled.toString()}</span>
      ),
    },
    {
      label: localize('ipnStatus'),
      template: r => <>{r?.ipnStatusDescription}</>,
    },
    {
      label: localize('ipnResponse'),
      template: r => <>{r?.ipnResponse}</>,
    },
    {
      label: localize('createdBy'),
      template: r => <>{r?.createdBy}</>,
    },
  ]

  const groupingColumns = [
    { value: 0, column: 'transactionTypeDescription' },
    { value: 1, column: 'accountNumber' },
    { value: 2, column: 'statusDescription' },
  ]

  const handleViewAuditLogs = item => {
    setAuditLogsDetails(item)
    setModalMode('auditLogs')
  }

  const handleCloseModal = () => {
    setModalMode(null)
  }
  return (
    <>
      <BreadCrumb
        tree={[
          localize('alternateChannelManagement'),
          localize('channelRequestLog'),
          localize('mobileBankingRequests'),
        ]}
      />
      {modalMode === 'auditLogs' && (
        <ViewAuditLogs
          handleClose={handleCloseModal}
          mode={modalMode}
          data={auditLogsDetails}
          title={auditLogsDetails?.accountNumber}
        />
      )}
      <div className="card bg-white rounded">
        <div className="card-body">
          {isBusy ? (
            <Loader />
          ) : error ? (
            <div className="col-12 d-flex align-items-center justify-content-center text-danger">
              <i className="uil uil-exclamation-circle m-2 fs-4"></i>
              <div className="fs-5 text-danger">Failed to Load Data</div>
            </div>
          ) : (
            <>
              <div className=" d-flex row align-items-center mb-3">
                <RecordsPerPage
                  className="col-2"
                  reqParams={reqParams}
                  setReqParams={setReqParams}
                />
                <div className="d-flex col-3 align-items-center">
                  <label className="text-capitalize me-2">
                    {localize('groupBy')}
                  </label>
                  <EnumsServerCombo
                    enumsKey="BankToMobileRequestPropertyGroup"
                    customContainerStyle={{
                      maxWidth: 'fit-content',
                      minWidth: 'fit-content',
                    }}
                    value={groupBy?.value}
                    onChange={({ label, value }) =>
                      setGroupBy({
                        label,
                        value,
                        column: groupingColumns[value]?.column,
                      })
                    }
                    sort
                  />
                </div>
                <div className="d-flex col-3 align-items-center">
                  <label htmlFor="search" className="me-1 text-capitalize">
                    {localize('dateRange')}
                  </label>
                  <DatePicker
                    value={{
                      startDate: reqParams?.startDate,
                      endDate: reqParams?.endDate,
                    }}
                    onSelect={v => {
                      if (v.custom) {
                        setReqParams({
                          ...reqParams,
                          customDate: true,
                          startDate: v.startDate,
                          endDate: v.endDate,
                        })
                      } else {
                        setReqParams({
                          ...reqParams,
                          customDate: false,
                          dateRequestsFilter: v.value,
                        })
                      }
                    }}
                  />
                </div>
                <div className="d-flex col-4 align-items-center justify-content-end ">
                  <label className="text-capitalize me-2">
                    {localize('search')}
                  </label>
                  <EnumsServerCombo
                    sort
                    customContainerStyle={{
                      maxWidth: 'fit-content',
                      minWidth: '150px',
                    }}
                    value={reqParams?.bankToMobileTransactionRequestFilter}
                    enumsKey="BankToMobileTransactionRequestFilter"
                    onChange={({ value }) =>
                      setReqParams({
                        ...reqParams,
                        bankToMobileTransactionRequestFilter: value,
                        pageIndex: 0,
                      })
                    }
                  />
                  <input
                    type="text"
                    className="form-control ms-2"
                    style={{ width: '100px' }}
                    value={reqParams?.text}
                    onChange={e =>
                      setReqParams({
                        ...reqParams,
                        text: e.target.value,
                        pageIndex: 0,
                      })
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        fetchRequestLog()
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary ms-1"
                    onClick={() => {
                      fetchRequestLog()
                    }}
                  >
                    <i className="uil uil-filter"></i>
                  </button>
                </div>
              </div>
              <GroupedTable
                columns={columns}
                data={requestLogs?.pageCollection || []}
                groupBy={groupBy}
                contextMenu={r => (
                  <>
                    <div
                      className="ctxt-menu-item text-capitalize"
                      onClick={() => {
                        handleViewAuditLogs(r)
                      }}
                    >
                      <i className="uil uil-server"></i>
                      <span>{localize('viewAuditLog')}</span>
                    </div>
                  </>
                )}
              />
              <Pager
                itemsCount={requestLogs?.itemsCount}
                pageSize={reqParams.pageSize}
                pageIndex={reqParams.pageIndex}
                pageBatchSize={5}
                onPageChange={p => setReqParams({ ...reqParams, pageIndex: p })}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default MobileBankingRequests
